import { FabricMimeTypes } from '@fabric-app/types/resources';
import { v4 } from 'uuid';
import { getExtensionFromFileName } from '@fabric-app/utils/manipulation';
import { ProcessingState } from '@fabric/woody-client';

const getMimeTypeByNewResourceData = (data) => {
  switch (data.createType) {
    case "bookmark":
      return FabricMimeTypes.VND_FABRIC_BOOKMARK;
    case "folder":
      return FabricMimeTypes.VND_FABRIC_FOLDER;
    case "note":
    case "voiceNote":
      return FabricMimeTypes.VND_FABRIC_NOTEPAD;
    case "file":
      return data.body.mimeType;
    case "highlight":
      return FabricMimeTypes.VND_FABRIC_HIGHLIGHT;
  }
};
const createOptimisticResource = (newResource, user = {
  id: v4(),
  name: null,
  pictureUrlCdn: null
}, options) => {
  const optimisticResourceId = options?.id || v4();
  const createdModifiedAt = (/* @__PURE__ */ new Date()).toISOString();
  const base = {
    id: optimisticResourceId,
    draftId: optimisticResourceId,
    isDraft: true,
    publishToken: null,
    name: "name" in newResource.body ? newResource.body.name || "" : "",
    mimeType: getMimeTypeByNewResourceData(newResource),
    stateProcessing: ProcessingState.PENDING,
    originUrl: "url" in newResource.body && newResource.body.url || null,
    isDirectShared: false,
    isShared: false,
    isReadonly: false,
    tags: "tags" in newResource.optimisticData ? newResource.optimisticData.tags : [],
    createdAt: createdModifiedAt,
    modifiedAt: createdModifiedAt,
    user,
    parent: newResource.optimisticData.parent,
    label: null,
    isHidden: false,
    ancestors: [],
    icon: null,
    cover: null,
    description: null
  };
  const commentRelatedProperties = "comment" in newResource.body && (newResource.body.comment?.annotation || newResource.body.comment?.content) ? {
    commentPinned: {
      // TODO draft comment
      id: v4(),
      user,
      userId: user?.id ?? null,
      content: newResource.body.comment.content || "",
      annotation: newResource.body.comment.annotation || {},
      createdAt: createdModifiedAt,
      modifiedAt: createdModifiedAt,
      resourceId: optimisticResourceId
    },
    commentCount: 1
  } : {
    commentPinned: null,
    commentCount: 0
  };
  const fileRelatedProperties = newResource.createType === "file" ? {
    size: newResource.optimisticData.file.size,
    extension: getExtensionFromFileName(newResource.optimisticData.file.name),
    thumbnail: null,
    fileUrl: newResource.body.attachment.filename,
    mimeType: newResource.optimisticData.file.type
  } : {
    extension: null,
    size: null,
    thumbnail: null,
    fileUrl: null,
    mimeType: null
  };
  const baseResource = {
    ...base,
    ...fileRelatedProperties,
    ...commentRelatedProperties,
    shareToken: null,
    pins: [],
    jobsProcessing: {
      attributes: ProcessingState.PENDING
    },
    root: null,
    preferences: {},
    cover: null,
    url: ""
  };
  if (newResource.createType === "folder") {
    const result = {
      ...baseResource,
      kind: "folder",
      mimeType: FabricMimeTypes.VND_FABRIC_FOLDER,
      data: {},
      stateProcessing: ProcessingState.COMPLETED
    };
    return result;
  }
  if (newResource.createType === "file") {
    const baseData = {
      infected: false,
      summary: null,
      attributes: null,
      extensionContext: void 0,
      webpage: null,
      ocr: null,
      caption: null,
      dominantColors: []
    };
    const thumbnailUrl = newResource.optimisticData.thumbnailUrl;
    if (newResource.optimisticData.file.type.startsWith("image/")) {
      const result = {
        ...baseResource,
        kind: "image",
        data: baseData,
        thumbnail: thumbnailUrl ? {
          sm: thumbnailUrl,
          md: thumbnailUrl,
          lg: thumbnailUrl,
          xl: thumbnailUrl,
          original: thumbnailUrl
        } : null
      };
      return result;
    }
    return {
      ...baseResource,
      kind: "default",
      data: baseData
    };
  }
  if (newResource.createType === "note" || newResource.createType === "voiceNote") {
    const result = {
      ...baseResource,
      kind: "notepad",
      mimeType: FabricMimeTypes.VND_FABRIC_NOTEPAD,
      data: {
        caption: null,
        attributes: {
          author: user?.name || "You"
        },
        preview: {
          content: newResource.optimisticData.preview.content || ""
        }
      }
    };
    return result;
  }
  if (newResource.createType === "bookmark") {
    const result = {
      ...baseResource,
      kind: "bookmark",
      mimeType: FabricMimeTypes.VND_FABRIC_BOOKMARK,
      data: {
        transcript: null,
        caption: null,
        dominantColors: [],
        attributes: {
          pageTitle: newResource.body.name || "Untitled",
          pageUrl: newResource.body.url
        },
        extensionContext: null,
        webpage: newResource.optimisticData.linkPreview ? {
          ...newResource.optimisticData.linkPreview,
          html: null,
          reader: null,
          metadata: newResource.optimisticData.linkPreview.metadata,
          screenshot: null,
          image: newResource.optimisticData.linkPreview.image ? {
            ...newResource.optimisticData.linkPreview.image,
            mime: null
          } : null,
          favicon: newResource.optimisticData.linkPreview.favicon ? {
            ...newResource.optimisticData.linkPreview.favicon,
            mime: null
          } : null
        } : null
      },
      thumbnail: newResource.optimisticData.linkPreview?.image ? {
        sm: newResource.optimisticData.linkPreview.image.url,
        md: newResource.optimisticData.linkPreview.image.url,
        lg: newResource.optimisticData.linkPreview.image.url,
        xl: newResource.optimisticData.linkPreview.image.url,
        original: newResource.optimisticData.linkPreview.image.url
      } : null
    };
    return result;
  }
  if (newResource.createType === "highlight") {
    const result = {
      ...baseResource,
      kind: "highlight",
      mimeType: FabricMimeTypes.VND_FABRIC_HIGHLIGHT,
      data: {
        caption: null,
        attributes: {
          pageTitle: newResource.body.extensionContext.pageTitle ?? null,
          pageUrl: newResource.body.extensionContext.pageUrl ?? null
        },
        webpage: newResource.optimisticData.linkPreview ? {
          ...newResource.optimisticData.linkPreview,
          html: null,
          reader: null,
          metadata: newResource.optimisticData.linkPreview.metadata,
          screenshot: null,
          image: newResource.optimisticData.linkPreview.image ? {
            ...newResource.optimisticData.linkPreview.image,
            mime: null
          } : null,
          favicon: newResource.optimisticData.linkPreview.favicon ? {
            ...newResource.optimisticData.linkPreview.favicon,
            mime: null
          } : null
        } : null,
        extensionContext: {
          ...newResource.body.extensionContext,
          text: newResource.body.extensionContext.text || "",
          pageTitle: newResource.body.extensionContext.pageTitle || "",
          pageUrl: newResource.body.extensionContext.pageUrl || "",
          textRange: newResource.body.extensionContext.textRange ?? {
            start: {
              closestPath: "",
              sentence: ""
            },
            end: {
              closestPath: "",
              sentence: ""
            },
            endOffset: 0,
            startOffset: 0
          }
        }
      }
    };
    return result;
  }
  throw new Error("Invalid resource type to create optimistic resource");
};

export { createOptimisticResource };
//# sourceMappingURL=createOptimisticResource.js.map
